
import { Component, Prop, Watch } from 'vue-property-decorator';
import TableFilterable from '@/components/reports-v3/components/filterables/TableFilterable.vue';
import FilterWidget from '@/components/reports-v3/components/codedWidgets/shared/FilterWidget.vue';
import { PermissionsGroup } from '@/store/models.def';
import { TableItemFormatter } from '@/components/reports-v3/components/elements/charts/helpers/tableItemFormatter';
import Checkbox from 'primevue/checkbox';
import moment from 'moment';
import gdbx from '@/store/modules/gdbx';
import { getTableName } from '@/worker/db/sqlHelpers';

@Component({
	components: {
		TableFilterable,
		Checkbox,
	},
})
export default class StockAvailabilityTable extends FilterWidget {

	public get permissionIds(): PermissionsGroup[] {
		return ['stocks'];
	}

	public get exportFileName() {
		const formattedDate = moment().format('DD MMM YY');
		return 'Stock Availability' + '_' + formattedDate;
	}

	public get dateFormatted() {
		return 'As Of ' + moment().format('DD MMM YY');
	}

	public get expensiveHook() {
		const { selectedStockItems, selectedLocations, isShowReorderAdvice } = this;
		return JSON.stringify([
			selectedStockItems,
			selectedLocations,
			isShowReorderAdvice,
		]);
	}

	public get newTableFields(): any[] {
		let table = [
			{
				key: 'location',
				sortable: true,
				stickyColumn: true,
			},
			{
				key: 'item_code',
				sortable: true,
				stickyColumn: true,
			},
			{
				key: 'item_description',
				sortable: true,
				stickyColumn: true,
			},
			{
				key: 'UOM',
				sortable: true,
				stickyColumn: true,
			},
			{
				key: 'on-hand_qty',
				sortable: true,
				stickyColumn: true,
				formatter: TableItemFormatter.basicInteger as any,
			},
		];

		if (this.isShowReorderAdvice) {
			table = [
				{
					key: 'item_code',
					sortable: true,
					stickyColumn: true,
				},
				{
					key: 'item_description',
					sortable: true,
					stickyColumn: true,
				},
				{
					key: 'UOM',
					sortable: true,
					stickyColumn: true,
				},
				{
					key: 'unit_cost',
					sortable: true,
					stickyColumn: true,
					formatter: TableItemFormatter.currencySplit as any,
				},
				{
					key: 'on-hand_qty',
					sortable: true,
					stickyColumn: true,
					formatter: TableItemFormatter.basicInteger as any,
				},
				{
					key: 'P_O',
					sortable: true,
					stickyColumn: true,
				},
				{
					key: 'S_O',
					sortable: true,
					stickyColumn: true,
				},
				{
					key: 'available',
					sortable: true,
					stickyColumn: true,
					formatter: TableItemFormatter.basicInteger as any,
				},
				{
					key: 'reorder_advice',
					sortable: true,
					stickyColumn: true,
					formatter: TableItemFormatter.basicInteger as any,
				},
				{
					key: 'reorder_level',
					sortable: true,
					stickyColumn: true,
					formatter: TableItemFormatter.basicInteger as any,
				},
				{
					key: 'reorder_qty',
					sortable: true,
					stickyColumn: true,
					formatter: TableItemFormatter.basicInteger as any,
				},
			];
		}
		return table;
	}

	@Prop({ default: () => [] }) public selectedLocations!: string[];

	public isShowReorderAdvice: boolean = false;

	public filterIds: Array<
		| 'date'
		| 'dateAsOf'
		| 'dateRange'
		| 'stockItems'
		| 'agents'
		| 'customers'
		| 'suppliers'
		| 'locations'
	> = ['stockItems', 'locations'];

	public tableItems: any[] = [];
	public tableFields: any[] = [];
	public stockCards: any[] = [];
	public result: any[] = [];
	public initialTableItems: any[] = [];
	public afterCalculationTableItems: any[] = [];

	public stockAvailabilityTable() {
		if (this.isShowReorderAdvice) {
			// Group by itemCode, sum quantities
			const grouped: { [key: string]: any } = {};

			for (const item of this.initialTableItems) {
				const stockCard = this.stockCards.find((a) => a.itemCode === item.itemCode);
				if (!stockCard) {
					continue;
				}

				if (!grouped[item.itemCode]) {
					grouped[item.itemCode] = {
						'item_code': item.itemCode,
						'item_description': stockCard.itemDesc,
						'UOM': item.uom,
						'on-hand_qty': 0,
						'reorder_level': item.reorderLevel,
						'reorder_qty': item.reorderQty,
						'P_O': 0,
						'S_O': 0,
						'unit_cost': item.avgCost,
					};
				}

				grouped[item.itemCode]['on-hand_qty'] += item.balance;
				grouped[item.itemCode].P_O += item.poAmount;
				grouped[item.itemCode].S_O += item.soAmount;
			}

			const groupedArray: any[] = [];
			for (const key of Object.keys(grouped)) {
				const obj = grouped[key];

				const available = obj['on-hand_qty'] + obj.P_O - obj.S_O;
				const reorderAdviceNum = obj.reorder_level + obj.reorder_qty - available;

				obj.available = available;
				obj.reorder_advice = reorderAdviceNum > 0 ? reorderAdviceNum : 0;
				obj._reorderAdviceNum = reorderAdviceNum; // Keep numeric value for filtering

				if (!obj.P_O) {
					obj.P_O = null;
				} else {
					obj.P_O = '+ ' + obj.P_O;
				}

				if (!obj.S_O) {
					obj.S_O = null;
				} else {
					obj.S_O = '- ' + obj.S_O;
				}

				groupedArray.push(obj);
			}

			// Sort by item_code
			groupedArray.sort((a, b) => a.item_code.localeCompare(b.item_code));

			this.loading = false;
			return this.afterCalculationTableItems = groupedArray.filter((a) => a._reorderAdviceNum > 0);
		}

		// Default: show by location
		const newArray: any[] = [];

		for (const item of this.initialTableItems) {
			const stockCard = this.stockCards.find((a) => a.itemCode === item.itemCode);
			if (!stockCard) {
				continue;
			}

			const obj: any = {
				'location': item.location,
				'item_code': item.itemCode,
				'item_description': stockCard.itemDesc,
				'UOM': item.uom,
				'on-hand_qty': item.balance,
			};

			newArray.push(obj);
		}

		// Sort by item_code then location
		newArray.sort((a, b) => {
			const itemCompare = a.item_code.localeCompare(b.item_code);
			if (itemCompare !== 0) {
				return itemCompare;
			}
			return a.location.localeCompare(b.location);
		});

		this.loading = false;
		return this.afterCalculationTableItems = newArray;
	}

	public async expensiveCalc() {
		const jobId = this.currentJobId;
		this.generateLoadingText(0.2, 0.4);

		const queryHead = await this._ensureTablesLoaded(
			['stockReorderAdvise', 'globalStocks'] as any[],
		);
		this.checkInterrupted(jobId);
		this.generateLoadingText(0.4, 0.7);

		const reorderTable = getTableName('stockReorderAdvise');
		const globalStocksTable = getTableName('globalStocks');

		const escapeStr = (s: string) => s.replace(/'/g, '\'\'');
		const allStockItemsLen = (gdbx.allStockItems && gdbx.allStockItems.length) || 0;
		const stockItemFilter = this.selectedStockItems && this.selectedStockItems.length > 0
			&& this.selectedStockItems.length !== allStockItemsLen
			? ` AND "CODE" IN (${this.selectedStockItems.map((s: string) => `'${escapeStr(s)}'`).join(', ')})`
			: '';
		const locationFilter = this.selectedLocations && this.selectedLocations.length > 0
			? ` AND "LOCATION" IN (${this.selectedLocations.map((s: string) => `'${escapeStr(s)}'`).join(', ')})`
			: '';

		const [reorderRows, descRows] = await Promise.all([
			queryHead.rawQuery(`
				SELECT "LOCATION" as location, "CODE" as itemCode,
					"STOCKGROUP" as stockGroup, TRY_CAST("BALQTY" AS DOUBLE) as balance,
					"UOM" as uom, TRY_CAST("REORDERLEVEL" AS DOUBLE) as reorderLevel,
					TRY_CAST("REORDERQTY" AS DOUBLE) as reorderQty,
					TRY_CAST("OSSOBASEUOMQTY" AS DOUBLE) as soAmount,
					TRY_CAST("OSPOBASEUOMQTY" AS DOUBLE) as poAmount,
					TRY_CAST("AVGUNITCOST" AS DOUBLE) as avgCost
				FROM ${reorderTable}
				WHERE 1=1${stockItemFilter}${locationFilter}
			`),
			queryHead.rawQuery(`
				SELECT "CODE" as itemCode, "DESCRIPTION" as itemDesc
				FROM ${globalStocksTable}
			`),
		]);
		this.checkInterrupted(jobId);
		this.generateLoadingText(0.7, 0.9);

		// Build description map from globalStocks
		const descMap: { [key: string]: string } = {};
		for (const row of descRows) {
			descMap[String(row.itemCode)] = String(row.itemDesc || '');
		}

		// Build stockCards-compatible array for use in stockAvailabilityTable()
		this.stockCards = descRows.map((r: any) => ({
			itemCode: String(r.itemCode || ''),
			itemDesc: String(r.itemDesc || ''),
		}));

		// Build initialTableItems from query results
		this.initialTableItems = reorderRows.map((r: any) => ({
			location: String(r.location || ''),
			itemCode: String(r.itemCode || ''),
			stockGroup: String(r.stockGroup || ''),
			balance: Number(r.balance) || 0,
			uom: String(r.uom || ''),
			reorderLevel: Number(r.reorderLevel) || 0,
			reorderQty: Number(r.reorderQty) || 0,
			soAmount: Number(r.soAmount) || 0,
			poAmount: Number(r.poAmount) || 0,
			avgCost: Number(r.avgCost) || 0,
		}));

		this.stockAvailabilityTable();

		this.saveHistory('stockCards', 'initialTableItems', 'afterCalculationTableItems');
		this.generateLoadingText(1);
	}
}
